import { concat } from '@ember/helper';
import { getSingleValueForTasField } from '../../utils/tuition-assistance/fields.ts';
import { hash } from '@ember/helper';
import { registerDestructor } from '@ember/destroyable';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import Container from './limits-summary/container.gts';
import { divide } from 'tio-ui/math';
import ErrorAlert from './limits-summary/error-alert.gts';
import LineItem from './limits-summary/line-item.gts';
import safeFormatNumber from '../../helpers/safe-format-number.ts';
import type { LimitsStatusDataSignature } from '../../services/tuition-assistance.ts';
import type { WithBoundArgs } from '@glint/template';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type IntlService from 'ember-intl/services/intl';
import type Owner from '@ember/owner';
import type { TasApplicationExtension as TasApplication } from 'tio-data/data/resources/tas-application.ext';
import type TuitionAssistanceService from '../../services/tuition-assistance.ts';

const number = Number;

export interface TuitionAssistanceLimitsSummarySignature {
  Args: {
    application: TasApplication;
    employee: EmployeeModel;
    approvedTotalToValidate?: number;
    approvedCourseCashTotalToValidate?: number;
    labelClass?: string;
    valueClass?: string;
    ignoreLimitsValidation?: boolean;
    paymentMethod?: string;
    shouldFetchLimits?: boolean;
  };
  Blocks: {
    default: [
      {
        Container: WithBoundArgs<typeof Container, 'title' | 'titleClass'>;
        LineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass' | 'sign'
        >;
        MaxAnnualBenefitLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        BenefitPaidForYearLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        RemainingBenefitLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        ApplicationAmountRequestedLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        ApplicationAmountBasedOnGPALineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        LimitsAlertForEmployeeIfApplicable: WithBoundArgs<
          typeof ErrorAlert,
          'showError' | 'errorMessage' | 'explanation'
        >;
        LimitsAlertForApproverIfApplicable: WithBoundArgs<
          typeof ErrorAlert,
          'showError' | 'errorMessage' | 'explanation'
        >;
        ApplicationAmountRequestedWithoutScholarshipsLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass' | 'sign'
        >;
        ScholarshipAmountLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass' | 'sign'
        >;
        ApplicationCourseCashSubtotalLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        ApplicationAllOtherExpensesSubtotalLineItem: WithBoundArgs<
          typeof LineItem,
          'label' | 'value' | 'isLoading' | 'labelClass' | 'valueClass'
        >;
        hasLimitsErrorForEmployee: boolean;
        hasLimitsErrorForApprover: boolean;
        isDisabledSubmitForEmployee: boolean;
        isDisabledSubmitForApprover: boolean;
        isUnlimitedMaximumAnnualBenefit: boolean;
        isUnlimitedRemainingBenefit: boolean;
        isCredits: boolean;
        refreshData: () => void;
      },
    ];
  };
  Element: HTMLElement;
}

export default class TuitionAssistanceLimitsSummaryComponent extends Component<TuitionAssistanceLimitsSummarySignature> {
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare intl: IntlService;

  @tracked private _limitStatus: LimitsStatusDataSignature | undefined;
  @tracked isLoading = false;
  getSingleValueForTasField = getSingleValueForTasField;

  constructor(owner: Owner, args: TuitionAssistanceLimitsSummarySignature['Args']) {
    super(owner, args);
    this.tuitionAssistance.registerLimitsSummaryComponent(this);
    this.initializeData();

    registerDestructor(this, () => this.tuitionAssistance.unregisterLimitsSummaryComponent(this));
  }

  get shouldFetchLimits(): boolean {
    return this.args.shouldFetchLimits ?? true;
  }

  async initializeData() {
    this.isLoading = true;
    if (this.args.application && this.args.employee) {
      await this.loadLimitStatusTask();
    }
    this.isLoading = false;
  }

  loadLimitStatusTask = async () => {
    const { employee, application } = this.args;
    if (!employee || !application) return;

    try {
      this._limitStatus = await this.tuitionAssistance.getV2LimitsStatus(employee, application);
    } catch (error) {
      console.error('Error loading V2 limits status:', error);
    }
  };

  get limitStatus(): LimitsStatusDataSignature | undefined {
    return this._limitStatus;
  }

  get isCredits() {
    return this._limitStatus?.limit_capacity === 'TAS.LimitCapacity.CREDITS';
  }

  get maximumAnnualBenefit() {
    const details = this._limitStatus?.details;
    if (!details) return null;
    // Use the most specific non-null annual limit (scheme = program limit)
    return (
      details.scheme?.annual_limit ??
      details.batch?.annual_limit ??
      details.product?.annual_limit ??
      details.company?.annual_limit ??
      null
    );
  }

  get isUnlimitedMaximumAnnualBenefit() {
    if (!this._limitStatus) {
      return false;
    }
    return this.maximumAnnualBenefit === null;
  }

  get remainingBenefit() {
    return this.limitStatus?.available ?? null;
  }

  get isUnlimitedRemainingBenefit() {
    if (!this.limitStatus) {
      return false;
    }
    return this.remainingBenefit === null;
  }

  get totalBenefitPaidForYear() {
    const details = this.limitStatus?.details;
    if (!details) return 0;
    // Use the same tier-priority as maximumAnnualBenefit to ensure usage units match the limit units
    return (
      details.scheme?.annual_usage ??
      details.batch?.annual_usage ??
      details.product?.annual_usage ??
      details.company?.annual_usage ??
      0
    );
  }

  get originalAmountRequested() {
    if (this.isCredits) {
      return this.args.application?.totalCredits || 0;
    }
    return this.args.application?.requestedTotal || 0;
  }

  get currentApprovedTotal() {
    if (this.isCredits) {
      return this.args.application?.totalCredits || 0;
    }
    const base =
      this.args.approvedTotalToValidate ??
      this.args.application.approvedTotal ??
      this.originalAmountRequested;
    const courseCashApproved = this.isCourseCashPaymentMethod
      ? (this.args.approvedCourseCashTotalToValidate ?? 0)
      : 0;

    return base + courseCashApproved;
  }

  get isValidOriginalAmountRequested() {
    if (this.isUnlimitedRemainingBenefit) {
      return true;
    }
    return (this.remainingBenefit || 0) - this.originalAmountRequested >= 0;
  }

  get isValidCurrentApprovedTotal() {
    if (this.isUnlimitedRemainingBenefit) {
      return true;
    }
    return (this.remainingBenefit || 0) - this.currentApprovedTotal >= 0;
  }

  get isAllowedSubmissionBeyondLimits() {
    if (this.args.application.ignorePolicies) {
      return true;
    }
    return (
      getSingleValueForTasField(
        'ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT',
        this.args.application.tasProgramInstance.tasProgramTemplate.fields
      ) || false
    );
  }

  get hasLimitsErrorForEmployee() {
    if (
      this.isAllowedSubmissionBeyondLimits ||
      this.isLoading ||
      this.args.application?.isDormantState ||
      !this.shouldFetchLimits
    ) {
      return false;
    }
    return !this.isValidOriginalAmountRequested;
  }

  get hasLimitsErrorForApprover() {
    if (
      this.isLoading ||
      this.args.application?.isDormantState ||
      this.args.ignoreLimitsValidation
    ) {
      return false;
    }
    return !this.isValidCurrentApprovedTotal;
  }

  get isDisabledSubmitForEmployee() {
    if (this.isAllowedSubmissionBeyondLimits) {
      return false;
    }
    return this.hasLimitsErrorForEmployee;
  }

  get isDisabledSubmitForApprover() {
    if (this.isAllowedSubmissionBeyondLimits) {
      return false;
    }
    return this.hasLimitsErrorForApprover;
  }

  get reductionNeededToOriginalAmountRequested() {
    if (this.isValidOriginalAmountRequested) {
      return 0;
    }
    return Math.abs((this.remainingBenefit || 0) - this.originalAmountRequested);
  }

  get reductionNeededToCurrentApprovedTotal() {
    if (this.isValidCurrentApprovedTotal) {
      return 0;
    }
    return Math.abs((this.remainingBenefit || 0) - this.currentApprovedTotal);
  }

  get formattedReductionNeededIfApplicable() {
    if (this.isCredits) {
      return `${this.reductionNeededToOriginalAmountRequested} credits`;
    }
    return this.intl.formatNumber(this.reductionNeededToOriginalAmountRequested / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  }

  get formattedReductionNeededForApprover() {
    if (this.isCredits) {
      return `${this.reductionNeededToCurrentApprovedTotal} credits`;
    }
    return this.intl.formatNumber(this.reductionNeededToCurrentApprovedTotal / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  }

  get noRemainingBenefitsMessageForEmployee() {
    return "Unfortunately, you've used up all benefits for the annual period (as defined by the dates you provided). This means you will not be able to submit a new application until the next annual period.";
  }

  get noRemainingBenefitsMessageForApprover() {
    return 'Unfortunately, the employee has used up all benefits for the annual period in which they are submitting their course(s). They will not be able to submit a new application until the next annual period.';
  }

  get limitsErrorMessageForEmployee() {
    return (this.remainingBenefit || 0) <= 0
      ? this.noRemainingBenefitsMessageForEmployee
      : `You have exceeded your employer's maximum limit. Please reduce your requested amount by ${this.formattedReductionNeededIfApplicable}`;
  }

  get limitsErrorMessageForApprover() {
    if (this.isAllowedSubmissionBeyondLimits) {
      return "The current application total exceeds the employee's maximum limit. This program is configured to allow submission of applications that exceed limits.";
    }
    if (this.isCredits) {
      return '<span class="font-semibold">Note</span>: The total credits for this application exceed the available credit limit. Approval above this limit is not permitted.';
    }
    return '<span class="font-semibold">Note</span>: The requested or entered reimbursement amount exceeds the allowable limit. Approval above this limit is not permitted.';
  }

  get activeLimitName() {
    const rawLimitName = this.limitStatus?.soft_limit || '';
    switch (rawLimitName) {
      case 'company_annual':
        return 'company annual limit';
      case 'company':
        return 'company limit';
      case 'product_annual':
        return 'Tuition Assistance annual limit';
      case 'product':
        return 'Tuition Assistance limit';
      case 'batch_annual':
        return 'Tuition Assistance Group annual limit';
      case 'batch':
        return 'Tuition Assistance Group limit';
      case 'scheme_annual':
        return 'program annual limit';
      case 'scheme':
        return 'program limit';
      case 'none':
        return '';
      default:
        return rawLimitName;
    }
  }

  get limitsErrorExplanationForEmployee() {
    const additionalText =
      // @ts-expect-error: @Julia I think null means something here, but I'm not sure what
      this.remainingBenefit <= 0
        ? this.noRemainingBenefitsMessageForEmployee
        : `You are over the limit by ${this.formattedReductionNeededIfApplicable}. Please reduce by ${this.formattedReductionNeededIfApplicable} in order to be able to submit your application within the limit.`;
    return `You have exceeded the <span class="font-semibold">${this.activeLimitName}</span> from your employer benefit plan. <br> ${additionalText}`;
  }

  get limitsErrorExplanationForApprover() {
    // @ts-expect-error: @Julia I think null means something here, but I'm not sure what
    if (this.remainingBenefit <= 0) {
      return `The application total exceeds the <span class="font-semibold">${this.activeLimitName}</span> from the employee's benefit plan. <br> ${this.noRemainingBenefitsMessageForApprover}`;
    }

    const formatValue = (amount: number) => {
      if (this.isCredits) {
        return `${amount} credits`;
      }
      return this.intl.formatNumber(amount / 100, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      });
    };

    const formattedMaxBenefit = formatValue(this.maximumAnnualBenefit || 0);
    const formattedPaidThisYear = formatValue(this.totalBenefitPaidForYear);
    const formattedRemaining = formatValue(this.remainingBenefit || 0);
    const formattedRequestedAmount = formatValue(this.currentApprovedTotal);

    return `
      The application total exceeds the <span class="font-semibold">${this.activeLimitName}</span> from the employee's benefit plan.
      <br><br>
      <strong>Limit Details:</strong>
      <ul class="list-disc ml-5 mt-2">
        <li>Annual Maximum: ${formattedMaxBenefit}</li>
        <li>Already Paid This Year: ${formattedPaidThisYear}</li>
        <li>Remaining Available: ${formattedRemaining}</li>
        <li>Current Approved Total: ${formattedRequestedAmount}</li>
        <li class="tio-fg-danger font-semibold">Over Limit By: ${this.formattedReductionNeededForApprover}</li>
      </ul>
      <br>
      Please reduce the approved amount by ${this.formattedReductionNeededForApprover} in order to approve the application within the limit.
    `;
  }

  get totalBasedOnGPA() {
    if (this.isCredits) {
      return this.args.application.totalCredits;
    }
    return Math.min(this.tuitionAssistance.gpaBasedAmount, this.args.application.requestedTotal);
  }

  get paymentMethod() {
    if (this.args.paymentMethod) {
      return this.args.paymentMethod;
    }
    return this.args.application.paymentMethod || 'TAS.AssistanceManner.REIMBURSEMENT';
  }

  get isCourseCashPaymentMethod() {
    return this.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  get courseCashSubtotalBasedOnPaymentMethod() {
    const courseCashSubtotal = this.args.application.requestedCourseCashSubtotal;
    if (this.isCourseCashPaymentMethod) {
      return courseCashSubtotal;
    }
    return 0;
  }

  get nonCourseCashSubtotalBasedOnPaymentMethod() {
    const nonCourseCashSubtotal = this.args.application.requestedNonCourseCashSubtotal;
    if (this.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH') {
      return nonCourseCashSubtotal;
    }
    return this.args.application.requestedTotalAllCourses;
  }

  // TODO: There is an edge case now where an approver can edit the approved total
  // and it would alter the assistances' amounts.
  // We need to use the exposed retry function to refresh the data when the approver saves the edit.
  refreshData = async () => {
    this.isLoading = true;
    if (this.args.application && this.args.employee && this.shouldFetchLimits) {
      await this.loadLimitStatusTask();
    }
    this.isLoading = false;
  };

  <template>
    {{yield
      (hash
        Container=(component Container)
        LineItem=(component LineItem labelClass=@labelClass valueClass=@valueClass)
        ScholarshipAmountLineItem=(component
          LineItem
          label=(t "tuition_assistance.limits_summary.scholarships")
          value=(if
            this.isCredits
            (concat (safeFormatNumber @application.scholarshipsTotal style="decimal") " credits")
            (safeFormatNumber
              (divide @application.scholarshipsTotal 100) style="currency" currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
          sign="plus"
        )
        ApplicationAmountRequestedLineItem=(component
          LineItem
          label=(t "tuition_assistance.limits_summary.total_request")
          value=(if
            this.isCredits
            (concat (safeFormatNumber @application.totalCredits style="decimal") " credits")
            (safeFormatNumber
              (divide @application.requestedTotal 100) style="currency" currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        ApplicationAmountBasedOnGPALineItem=(component
          LineItem
          label=(t "tuition_assistance.limits_summary.total_request")
          value=(if
            this.isCredits
            (concat (safeFormatNumber @application.totalCredits style="decimal") " credits")
            (safeFormatNumber (divide this.totalBasedOnGPA 100) style="currency" currency="USD")
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        ApplicationAmountRequestedWithoutScholarshipsLineItem=(component
          LineItem
          label=(t "tuition_assistance.limits_summary.requested_amount")
          value=(if
            this.isCredits
            (concat (safeFormatNumber @application.totalCredits style="decimal") " credits")
            (safeFormatNumber
              (divide @application.requestedTotalAllCourses 100) style="currency" currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        MaxAnnualBenefitLineItem=(component
          LineItem
          label=(if
            this.isCredits
            (t "tuition_assistance.limits_summary.eligible_credit_limit")
            (t "tuition_assistance.limits_summary.program_limit")
          )
          value=(if
            this.isCredits
            (if
              this.isUnlimitedRemainingBenefit
              ""
              (concat (safeFormatNumber (number this.remainingBenefit) style="decimal") " credits")
            )
            (if
              this.isUnlimitedMaximumAnnualBenefit
              ""
              (safeFormatNumber
                (divide (number this.maximumAnnualBenefit) 100) style="currency" currency="USD"
              )
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        BenefitPaidForYearLineItem=(component
          LineItem
          label=(if
            this.isCredits
            (t "tuition_assistance.limits_summary.pending_credit_limit")
            (t "tuition_assistance.limits_summary.benefit_paid_and_approved")
          )
          value=(if
            this.isCredits
            (concat (safeFormatNumber this.totalBenefitPaidForYear style="decimal") " credits")
            (safeFormatNumber
              (divide this.totalBenefitPaidForYear 100) style="currency" currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        RemainingBenefitLineItem=(component
          LineItem
          label=(if
            this.isCredits
            (t "tuition_assistance.limits_summary.remaining_credit_limit")
            (t "tuition_assistance.limits_summary.benefit_remaining")
          )
          value=(if
            this.isUnlimitedRemainingBenefit
            ""
            (if
              this.isCredits
              (concat (safeFormatNumber (number this.remainingBenefit) style="decimal") " credits")
              (safeFormatNumber
                (divide (number this.remainingBenefit) 100) style="currency" currency="USD"
              )
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        LimitsAlertForEmployeeIfApplicable=(component
          ErrorAlert
          showError=this.hasLimitsErrorForEmployee
          errorMessage=this.limitsErrorMessageForEmployee
          explanation=this.limitsErrorExplanationForEmployee
        )
        LimitsAlertForApproverIfApplicable=(component
          ErrorAlert
          showError=this.hasLimitsErrorForApprover
          errorMessage=this.limitsErrorMessageForApprover
          explanation=this.limitsErrorExplanationForApprover
        )
        ApplicationAllOtherExpensesSubtotalLineItem=(component
          LineItem
          value=(if
            this.isCredits
            (safeFormatNumber this.nonCourseCashSubtotalBasedOnPaymentMethod style="decimal")
            (safeFormatNumber
              (divide this.nonCourseCashSubtotalBasedOnPaymentMethod 100)
              style="currency"
              currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=@labelClass
          valueClass=@valueClass
        )
        ApplicationCourseCashSubtotalLineItem=(component
          LineItem
          value=(if
            this.isCredits
            (safeFormatNumber this.courseCashSubtotalBasedOnPaymentMethod style="decimal")
            (safeFormatNumber
              (divide this.courseCashSubtotalBasedOnPaymentMethod 100)
              style="currency"
              currency="USD"
            )
          )
          isLoading=this.isLoading
          labelClass=(concat @labelClass " " "flex")
          valueClass=@valueClass
        )
        hasLimitsErrorForApprover=this.hasLimitsErrorForApprover
        hasLimitsErrorForEmployee=this.hasLimitsErrorForEmployee
        isDisabledSubmitForEmployee=this.isDisabledSubmitForEmployee
        isDisabledSubmitForApprover=this.isDisabledSubmitForApprover
        isUnlimitedMaximumAnnualBenefit=this.isUnlimitedMaximumAnnualBenefit
        isUnlimitedRemainingBenefit=this.isUnlimitedRemainingBenefit
        isCredits=this.isCredits
        refreshData=this.refreshData
      )
    }}
  </template>
}
